import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { isSingleEventActivity } from 'shared';
import { useActivitySession } from '@/hooks/useActivities';
import { ErrorBlock, LoadingBlock } from '@/components/activities/ActivityUi';
import SessionBoard from '@/components/activities/SessionBoard';
import { useToast } from '@/components/useToast';
import { ToastContainer } from '@/components/Toast';

/**
 * One session, open in front of whoever is running it: the code to show, the
 * roster filling in, the visiting group and the session's details.
 */
export default function ActivitySessionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toasts, toast, dismiss } = useToast();

  const sessionId = Number(id);
  const validId = Number.isInteger(sessionId) && sessionId > 0;

  const sessionQuery = useActivitySession(validId ? sessionId : null);
  const session = sessionQuery.data;

  return (
    <>
      <Helmet>
        <title>{session ? `${session.activityName} | DIGGER` : 'Sessão | DIGGER'}</title>
      </Helmet>

      <div className="space-y-6">
        <Link
          to={session ? `/atividades/${session.activityId}` : '/atividades'}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {session ? session.activityName : 'Atividades'}
        </Link>

        {!validId && <ErrorBlock message="Sessão inválida." />}
        {validId && sessionQuery.isLoading && <LoadingBlock />}
        {validId && sessionQuery.isError && (
          <ErrorBlock message={sessionQuery.error?.message ?? 'Erro ao carregar a sessão.'} />
        )}

        {session && (
          <SessionBoard
            session={session}
            // An open day goes with its session, so there is no activity left
            // to go back to.
            onCancelled={() =>
              navigate(
                isSingleEventActivity(session.activityType)
                  ? '/atividades'
                  : `/atividades/${session.activityId}`,
                { replace: true },
              )
            }
            onSuccess={(msg) => toast.success(msg)}
            onError={(msg) => toast.error(msg)}
          />
        )}
      </div>

      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </>
  );
}
